<script setup lang="ts">
import ContainerForm, { type IContainerInputFormExposed } from '@/components/rc/shipments/forms/ContainerForm.vue'
import DamagesInputForm, { type IDamagesInputFormExposed } from '@/components/rc/shipments/forms/DamagesInputForm.vue'
import { useContainers } from '@/composables/rc/containers'
import { splitDamages } from '@/helpers/rc'
import { ContainerStatus, ContainerType } from '@/types/rc/containers'
import type { IContainerForm } from '@/types/rc/forms'
import type { IMinerIdentifier } from '@/types/rc/miners'
import { ShipmentType, type IContainer, type IDamageInstance } from '@/types/rc/shipments'
import { useQueryClient } from '@tanstack/vue-query'
import { ElMessage } from 'element-plus'
import { computed, ref, toRaw, watchEffect } from 'vue'

// Interface
const queryClient = useQueryClient()
const open = defineModel<boolean>()
const props = defineProps<{ containerId?: number; workOrderNumber?: string; kind?: ShipmentType }>()

// State
const activePanels = ref(['general-section', 'damages-section'])
const isUpdate = computed(() => !!props.containerId)
const isBuilding = computed(() => containerForm.value.status === ContainerStatus.Building)
const isAnyMutationPending = computed(() => createContainerPending.value || updateContainerPending.value)
const isLoadingState = computed(() => (isUpdate.value && containerDetailsPending.value) || isAnyMutationPending.value)

// Form state
const cleanState = (): IContainerFormState =>
  structuredClone({
    id: undefined,
    work_order: props.workOrderNumber ? { number: props.workOrderNumber } : undefined,
    customer: undefined,
    kind: props.kind ?? ShipmentType.Inbound,
    shipment: undefined,
    container_type: ContainerType.Box,
    miners_expected: 1,
    container_number: '',
    status: ContainerStatus.Building,
    miners: [] as IMinerIdentifier[],
    width: undefined,
    height: undefined,
    length: undefined,
    weight: undefined,
  })

/**
 * Stateless function to initialize the form state from container details (query result).
 * @param container the IContainer returned by the containerQuery
 */
const initialFormStateFromContainerDetails = (container: IContainer): IContainerFormState => ({
  id: container.id,
  work_order: container.work_order_number ? { number: container.work_order_number } : undefined,
  customer: {
    id: container.customer.id,
    name: container.customer.name,
  },
  kind: container.kind ?? ShipmentType.Inbound,
  shipment: container.shipment?.id,
  container_type: container.type,
  miners_expected: container.expected_miners ?? 1,
  container_number: container.number,
  status: container.status,
  miners: container.miners,
  width: container.width ?? undefined,
  height: container.height ?? undefined,
  length: container.length ?? undefined,
  weight: container.weight ?? undefined,
})

type IContainerFormState = Omit<IContainerForm, 'damages'>

// Omit the damages from the form state and zip them off from the damagesList at the end
const containerForm = ref<IContainerFormState>(cleanState())
const damagesList = ref<IDamageInstance[]>([])

// Form references
const damagesInputForm = ref<IDamagesInputFormExposed>()
const containerInputForm = ref<IContainerInputFormExposed>()

// Server bindings

const { containerDetailsQuery, createContainerMutation, updateContainerMutation } = useContainers()

const { mutateAsync: createContainer, isPending: createContainerPending } = createContainerMutation
const { mutateAsync: updateContainer, isPending: updateContainerPending } = updateContainerMutation

// Container details query - Reacts when containerId changes
const containerId = computed(() => props.containerId)
const queryActive = computed(() => !!isUpdate.value && !!open.value)
const { data: containerDetails, isPending: containerDetailsPending } = containerDetailsQuery(containerId, queryActive)

// When containerDetails are fetched, update the form state.
// Also make sure to run when the dialog is opened (immediate), to fill in existent state.
watchEffect(() => {
  if (containerDetails.value && containerDetails.value.id == containerId.value) {
    containerForm.value = initialFormStateFromContainerDetails(containerDetails.value)
    damagesList.value = [...containerDetails.value.damages]
  }
})

const sendUpdate = () => {
  // If we're updating we must have containerDetails filled in
  if (!containerDetails.value) return
  const [newDamages, deletedDamages] = splitDamages(damagesList.value.map(toRaw), toRaw(containerDetails.value))
  updateContainer(
    {
      id: containerDetails.value.id,
      container: {
        ...containerForm.value,
        miner_ids: containerForm.value.miners.map((m) => m.id),
        shipment: containerForm.value.shipment ?? null,
        // @ts-expect-error - newDamages is not typed
        newDamages,
        // @ts-expect-error - deletedDamages is not typed
        deletedDamages,
      },
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['work-order-logistics', containerDetails.value.work_order_number] })
        open.value = false
      },
    }
  )
}

const sendCreate = () =>
  createContainer(
    {
      ...containerForm.value,
      damages: damagesList.value,
      shipment: containerForm.value.shipment ?? null,
    },
    {
      onSuccess: () => {
        open.value = false
      },
    }
  )

const sendForm = async () => {
  if (isUpdate.value) {
    sendUpdate()
  } else {
    sendCreate()
  }
}

// UI handlers

const onSubmitForm = async () => {
  try {
    await containerInputForm.value?.validate()
    await damagesInputForm.value?.validate()
    await sendForm()
  } catch {
    ElMessage.error({ message: 'Please fill in all required fields.', grouping: true })
  }
}

// Reset shipment when kind (inbound/outbound) changes
const onKindChange = () => {
  containerForm.value.shipment = undefined
}

const onDialogOpen = () => {
  if (containerDetails.value && containerDetails.value.id == containerId.value) {
    containerForm.value = initialFormStateFromContainerDetails(containerDetails.value)
    damagesList.value = [...containerDetails.value.damages]
  } else {
    containerForm.value.kind = props.kind ?? ShipmentType.Inbound
  }
}

// Reset the form state when the dialog is closed
const onDialogClosed = () => {
  containerForm.value = cleanState()
  damagesList.value = []
  containerInputForm.value?.reset()
  damagesInputForm.value?.reset()
}
</script>

<template>
  <el-dialog
    v-model="open"
    width="570"
    class="dialog"
    :show-close="false"
    @open="onDialogOpen"
    @closed="onDialogClosed"
  >
    <template #header>
      <h3>{{ isUpdate ? 'Update' : 'Create' }} {{ containerForm.kind }} Container</h3>
    </template>
    <div v-loading="isLoadingState" class="dialog-body">
      <el-radio-group
        v-model="containerForm.kind"
        size="large"
        :disabled="isUpdate"
        fill="var(--fill-tertiary)"
        @change="onKindChange"
      >
        <el-radio-button :label="ShipmentType.Inbound" :value="ShipmentType.Inbound" />
        <el-radio-button :label="ShipmentType.Outbound" :value="ShipmentType.Outbound" />
      </el-radio-group>

      <el-collapse v-model="activePanels">
        <el-collapse-item name="general-section">
          <template #title>General</template>
          <ContainerForm
            ref="containerInputForm"
            v-model="containerForm"
            :is-update="isUpdate"
            :lock-work-order="!!workOrderNumber"
          />
        </el-collapse-item>
        <el-collapse-item name="damages-section">
          <template #title>Damages</template>
          <DamagesInputForm ref="damagesInputForm" v-model="damagesList" :disabled="!isBuilding" />
        </el-collapse-item>
      </el-collapse>
    </div>
    <template #footer>
      <el-button @click="open = false">Cancel</el-button>
      <el-button :loading="isAnyMutationPending" type="primary" @click="onSubmitForm">
        {{ isUpdate ? 'Save' : 'Create' }}
      </el-button>
    </template>
  </el-dialog>
</template>

<style lang="scss">
.dialog-body {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-spacing-xl);
}

.el-form-item {
  width: 100%;
}

// The big Inbound/Outbound buttons at the top
.el-radio-group {
  width: 100%;
  display: flex;
  fill: var(--fill-tertiary);

  .el-radio-button {
    flex-grow: 1;

    .el-radio-button__inner {
      width: 100%;
    }
  }
}

.el-collapse-item__header {
  font-size: var(--font-size-form-section-header);
  margin: var(--spacing-spacing-md) 0;
}
</style>
